import React, { useState, useEffect } from "react";
import { timeZoneList } from "../../../utils/timeZoneList";
import DateTimePickerStep from "../Common/DateTimePickerStep";
import EditTrialClassPopup from "./Components/EditTrialClassPopup";
import CancelConfirmPopup from "./Components/CancelConfirmPopup";
import CancelDonePopup from "./Components/CancelDonePopup";
import ReschedulePopup from "./Components/ReschedulePopup";
import { useRescheduleDemo, useCancelDemo, useSlots } from "../services/Api";

/**
 * EditClassFlow — orchestrates the edit trial class flow.
 *
 * Steps:
 *   'edit'                → EditTrialClassPopup (choose reschedule or cancel)
 *   'reschedule-datetime' → DateTimePickerStep  (pick new slot)
 *   'reschedule-done'     → ReschedulePopup     (success)
 *   'cancel-confirm'      → CancelConfirmPopup  (are you sure?)
 *   'cancel-done'         → CancelDonePopup     (success)
 *
 * Props:
 *  open         boolean
 *  onClose      function  — called when the whole flow is dismissed
 *  trialClass   { date, time, subject, mentorName, mentorAvatar }
 *  onCancelled  function  — called after cancel is confirmed (optional)
 *  onRescheduled function — called after reschedule is confirmed (optional)
 */
const EditClassFlow = ({
  open,
  onClose,
  trialClass = {},
  session = null,
  onCancelled,
  onRescheduled,
  onBookAgain,
  initialTimezone,
}) => {
  const [step, setStep] = useState("edit");
  const [timezoneValue, setTimezoneValue] = useState(initialTimezone);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const { mutate: rescheduleDemo } = useRescheduleDemo();
  const { mutate: cancelDemo } = useCancelDemo();
  const { allSlots, slotsLoading, fetchSlots } = useSlots();

  const courseId = session?.courseId ?? null;

  useEffect(() => {
    if (open && step === "reschedule-datetime") {
      fetchSlots({ courseId, timeZone: timezoneValue });
    }
  }, [open, step, courseId, timezoneValue, fetchSlots, session]);

  useEffect(() => {
    if (open) {
      setStep("edit");
      setRescheduleDate("");
      setTimezoneValue(initialTimezone);
    }
  }, [open, initialTimezone]);

  const handleRescheduleConfirm = async ({ slotId, slotDate, slotTime }) => {
    setRescheduleLoading(true);
    try {
      await rescheduleDemo({
        data: {
          studentId: session?.studentId,
          prevSlotId: session?.SlotId,
          curSlotId: slotId,
          timeZone: timezoneValue,
        },
      });
      setRescheduleDate(`${slotDate}`);
      setStep("reschedule-done");
      onRescheduled?.();
    } catch (e) {
      console.error("Reschedule failed", e);
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    try {
      await cancelDemo({
        data: {
          studentId: session?.studentId,
          id: session?.SlotId,
        },
      });
      setStep("cancel-done");
      onCancelled?.();
    } catch (e) {
      console.error("Cancel failed", e);
    } finally {
      setCancelLoading(false);
    }
  };

  const tzAbbr = (() => {
    const found = timeZoneList?.find((tz) => tz?.value === timezoneValue);
    if (!found) return "";
    const match = found?.timeZones?.match(/\(([^)]+)\)/);
    return match ? match[1] : "";
  })();

  const scheduledDateLabel =
    trialClass?.date && trialClass?.time
      ? `${trialClass?.date}, ${trialClass?.time}${tzAbbr ? ` ${tzAbbr}` : ""}`
      : "";

  return (
    <>
      <EditTrialClassPopup
        open={open && step === "edit"}
        onClose={onClose}
        trialClass={trialClass}
        onReschedule={() => setStep("reschedule-datetime")}
        onCancel={() => setStep("cancel-confirm")}
      />

      <DateTimePickerStep
        open={open && step === "reschedule-datetime"}
        onClose={onClose}
        onGoBack={() => setStep("edit")}
        onConfirm={handleRescheduleConfirm}
        timezoneValue={timezoneValue}
        timeZones={timeZoneList}
        onTimezoneChange={setTimezoneValue}
        courseId={courseId}
        allSlots={allSlots}
        slotsLoading={slotsLoading}
        submitting={rescheduleLoading}
        mode="reschedule"
      />

      <ReschedulePopup
        open={open && step === "reschedule-done"}
        onClose={onClose}
        subject={trialClass?.subject || ""}
        rescheduleDate={rescheduleDate}
        timezoneValue={timezoneValue}
        timeZoneList={timeZoneList}
        onKnowMentor={() => {}}
        onEvaluationTest={() => {}}
        onLearningActivity={() => {}}
      />

      <CancelConfirmPopup
        open={open && step === "cancel-confirm"}
        onClose={() => setStep("edit")}
        scheduledDate={scheduledDateLabel}
        onConfirmCancel={handleConfirmCancel}
        onProceedReschedule={() => setStep("reschedule-datetime")}
        loading={cancelLoading}
      />

      <CancelDonePopup
        open={open && step === "cancel-done"}
        onClose={onClose}
        onGoHome={onClose}
        onBookAgain={() => {
          onClose?.();
          onBookAgain?.();
        }}
      />
    </>
  );
};

export default EditClassFlow;
